import React, { useState, useCallback } from "react";
import styles from "./BulkImageCompressor.module.css";

const BulkImageCompressor = () => {
  const [images, setImages] = useState([]);
  const [compressedImages, setCompressedImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const [quality, setQuality] = useState(80);
  const [outputFormat, setOutputFormat] = useState("jpg");
  const [batchProgress, setBatchProgress] = useState({});
  const [resizeEnabled, setResizeEnabled] = useState(false);
  const [maxWidth, setMaxWidth] = useState(1920);
  const [maxHeight, setMaxHeight] = useState(1080);
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [settingsChanged, setSettingsChanged] = useState(false);

  // Reset compressed images when settings change
  const handleSettingChange = (setter) => (value) => {
    setter(value);
    if (compressedImages.length > 0) {
      setSettingsChanged(true);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length) {
      processImages(files);
    }
  }, []);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length) {
      processImages(files);
    }
  };

  const processImages = (files) => {
    setError(null);

    const imageFiles = files.filter((file) => file.type.match("image.*"));
    if (imageFiles.length === 0) {
      setError("Please upload image files");
      return;
    }

    // Limit to 20 files for performance
    if (imageFiles.length > 20) {
      setError("Maximum 20 images allowed at once for optimal performance");
      return;
    }

    const processedImages = [];
    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const newImage = {
            id: Math.random().toString(36).substr(2, 9),
            file,
            url: event.target.result,
            name: file.name,
            type: file.type,
            size: file.size,
            dimensions: { width: img.width, height: img.height },
            originalDimensions: { width: img.width, height: img.height },
          };
          processedImages.push(newImage);

          if (processedImages.length === imageFiles.length) {
            setImages(processedImages);
            setCompressedImages([]);
            setSettingsChanged(false);
          }
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Calculate new dimensions with aspect ratio
  const calculateNewDimensions = (originalWidth, originalHeight) => {
    if (!resizeEnabled) {
      return { width: originalWidth, height: originalHeight };
    }

    let newWidth = originalWidth;
    let newHeight = originalHeight;

    if (originalWidth > maxWidth || originalHeight > maxHeight) {
      const widthRatio = maxWidth / originalWidth;
      const heightRatio = maxHeight / originalHeight;
      const ratio = maintainAspectRatio
        ? Math.min(widthRatio, heightRatio)
        : widthRatio;

      newWidth = Math.round(originalWidth * ratio);
      newHeight = Math.round(originalHeight * ratio);
    }

    return { width: newWidth, height: newHeight };
  };

  // Convert data URL to blob
  const dataURLToBlob = (dataURL) => {
    try {
      const byteString = atob(dataURL.split(",")[1]);
      const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      return new Blob([ab], { type: mimeString });
    } catch (error) {
      console.error("Error converting data URL to blob:", error);
      return null;
    }
  };

  // Compress single image
  const compressImage = (image) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      const img = new Image();

      img.onload = () => {
        try {
          const newDimensions = calculateNewDimensions(img.width, img.height);
          canvas.width = newDimensions.width;
          canvas.height = newDimensions.height;

          const ctx = canvas.getContext("2d");

          // Fill with white background for transparent images
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          let mimeType, fileExtension;
          switch (outputFormat) {
            case "jpg":
              mimeType = "image/jpeg";
              fileExtension = "jpg";
              break;
            case "png":
              mimeType = "image/png";
              fileExtension = "png";
              break;
            case "webp":
              mimeType = "image/webp";
              fileExtension = "webp";
              break;
            default:
              mimeType = "image/jpeg";
              fileExtension = "jpg";
          }

          const compressedUrl = canvas.toDataURL(
            mimeType,
            outputFormat === "jpg" || outputFormat === "webp"
              ? quality / 100
              : undefined
          );

          const blob = dataURLToBlob(compressedUrl);
          if (!blob) {
            reject(new Error("Failed to create blob from compressed image"));
            return;
          }

          resolve({
            original: image,
            url: compressedUrl,
            blob: blob,
            name: `${image.name.split(".")[0]}_compressed.${fileExtension}`,
            size: blob.size,
            dimensions: { width: canvas.width, height: canvas.height },
            quality: quality,
            format: outputFormat.toUpperCase(),
            savings: Math.round(((image.size - blob.size) / image.size) * 100),
          });
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      img.src = image.url;
    });
  };

  // Batch compression
  const compressBatch = async () => {
    if (images.length === 0) return;

    setBatchProgress({});
    const results = [];

    try {
      for (let i = 0; i < images.length; i++) {
        setBatchProgress((prev) => ({
          ...prev,
          [images[i].id]: `Compressing ${i + 1}/${images.length}`,
        }));

        const result = await compressImage(images[i]);
        results.push(result);

        setBatchProgress((prev) => ({
          ...prev,
          [images[i].id]: `Completed ${i + 1}/${images.length}`,
        }));
      }

      setCompressedImages(results);
      setSettingsChanged(false); // Reset settings changed flag after compression
    } catch (error) {
      setError(`Compression failed: ${error.message}`);
    }
  };

  // Download single image
  const downloadImage = (compressedImage) => {
    try {
      if (!compressedImage.blob) {
        setError("No compressed image data available");
        return;
      }

      const url = URL.createObjectURL(compressedImage.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = compressedImage.name;
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      setError(`Download failed: ${error.message}`);
    }
  };

  // Download all images
  const downloadAllImages = () => {
    if (compressedImages.length === 0) {
      setError("No compressed images available to download");
      return;
    }

    compressedImages.forEach((compressedImage, index) => {
      setTimeout(() => {
        downloadImage(compressedImage);
      }, index * 300); // Increased delay to prevent browser blocking
    });
  };

  const resetAll = () => {
    setImages([]);
    setCompressedImages([]);
    setError(null);
    setBatchProgress({});
    setSettingsChanged(false);
  };

  const removeImage = (imageId) => {
    setImages((prev) => prev.filter((img) => img.id !== imageId));
    // If we remove an image, we should re-compress
    if (compressedImages.length > 0) {
      setSettingsChanged(true);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 bytes";
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  const totalOriginalSize = images.reduce(
    (sum, img) => sum + (img.size || 0),
    0
  );
  const totalCompressedSize = compressedImages.reduce(
    (sum, img) => sum + (img.size || 0),
    0
  );
  const totalSavings = totalOriginalSize - totalCompressedSize;
  const averageSavings =
    compressedImages.length > 0
      ? compressedImages.reduce((sum, img) => sum + (img.savings || 0), 0) /
        compressedImages.length
      : 0;

  // Determine button text and state
  const getCompressButtonText = () => {
    if (compressedImages.length === 0) {
      return "Compress All Images";
    } else if (settingsChanged) {
      return "Re-compress with New Settings";
    } else {
      return "Compress Again";
    }
  };

  const isCompressButtonDisabled =
    compressedImages.length > 0 && !settingsChanged;

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Bulk Image Compressor</h1>

      {/* Step Guide */}
      {images.length === 0 && (
        <div className={styles.stepsGuide}>
          <h2>Compress Multiple Images in One Click</h2>
          <div className={styles.steps}>
            <div className={styles.step}>
              <div className={styles.stepNumber}>1</div>
              <div className={styles.stepContent}>
                <h4>Upload Images</h4>
                <p>Select or drag & drop multiple image files</p>
              </div>
            </div>
            <div className={styles.step}>
              <div className={styles.stepNumber}>2</div>
              <div className={styles.stepContent}>
                <h4>Adjust Settings</h4>
                <p>Set compression quality and resize options</p>
              </div>
            </div>
            <div className={styles.step}>
              <div className={styles.stepNumber}>3</div>
              <div className={styles.stepContent}>
                <h4>Compress & Download</h4>
                <p>Reduce file sizes and download optimized images</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Section */}
      {images.length === 0 && (
        <div className={styles.uploadSection}>
          <div
            className={`${styles.dropArea} ${
              isDragging ? styles.dragOver : ""
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => document.getElementById("fileInput").click()}
          >
            <div className={styles.dropContent}>
              <div className={styles.uploadIcon}>🖼️</div>
              <h3 className={styles.dropTitle}>Drop your images here</h3>
              <p className={styles.dropText}>or</p>
              <input
                type="file"
                id="fileInput"
                accept="image/*"
                onChange={handleFileChange}
                className={styles.fileInput}
                multiple
              />
              <button className={styles.browseButton} type="button">
                Select Multiple Files
              </button>
              <p className={styles.supportedFormats}>
                Supports JPG, PNG, WEBP, GIF, BMP - Max 20 files
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Comprehensive Content Section - ADDED BELOW DRAG & DROP */}
      {images.length === 0 && (
        <div className={styles.contentSection}>
          <div className={styles.contentGrid}>
            {/* Why Bulk Compression Section */}
            <div className={styles.contentBlock}>
              <h2>Why Bulk Image Compression is Essential</h2>
              <p>
                In today's digital landscape, businesses and individuals
                regularly handle dozens or even hundreds of images
                simultaneously. Bulk compression eliminates the tedious process
                of optimizing images one by one, saving hours of manual work
                while ensuring consistent quality across your entire image
                library.
              </p>

              <div className={styles.benefitsGrid}>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>⏱️</div>
                  <h3>Time Efficiency</h3>
                  <p>
                    Process 20 images simultaneously instead of spending hours
                    on individual compression tasks. What takes 5 minutes
                    manually now completes in seconds.
                  </p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>📊</div>
                  <h3>Consistent Results</h3>
                  <p>
                    Apply uniform compression settings across all images,
                    eliminating variations that occur with manual processing and
                    ensuring professional consistency.
                  </p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>💾</div>
                  <h3>Storage Optimization</h3>
                  <p>
                    Reduce total image storage requirements by 60-80% while
                    maintaining visual quality, significantly lowering hosting
                    and storage costs.
                  </p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>🚀</div>
                  <h3>Performance Boost</h3>
                  <p>
                    Optimized images load faster, improving website performance
                    scores, user experience, and search engine rankings across
                    your entire site.
                  </p>
                </div>
              </div>
            </div>

            {/* Industry Applications */}
            <div className={styles.contentBlock}>
              <h2>Industry-Specific Applications</h2>
              <div className={styles.industryGrid}>
                <div className={styles.industryItem}>
                  <h4>🛒 E-commerce Platforms</h4>
                  <p>
                    Process hundreds of product images with consistent
                    compression. Reduce page load times by 40-60% while
                    maintaining product image quality for better conversion
                    rates.
                  </p>
                  <div className={styles.industryStats}>
                    <span>Typical Savings: 65-80%</span>
                    <span>Batch Size: 10-50 images</span>
                  </div>
                </div>
                <div className={styles.industryItem}>
                  <h4>📱 Mobile App Development</h4>
                  <p>
                    Optimize app assets for different screen densities. Reduce
                    APK/IPA file sizes significantly while ensuring crisp
                    graphics across all device resolutions.
                  </p>
                  <div className={styles.industryStats}>
                    <span>Typical Savings: 50-70%</span>
                    <span>Batch Size: 20-100 images</span>
                  </div>
                </div>
                <div className={styles.industryItem}>
                  <h4>📧 Email Marketing</h4>
                  <p>
                    Compress campaign images to stay within email size limits
                    while ensuring fast loading across all email clients and
                    devices.
                  </p>
                  <div className={styles.industryStats}>
                    <span>Typical Savings: 60-75%</span>
                    <span>Batch Size: 5-15 images</span>
                  </div>
                </div>
                <div className={styles.industryItem}>
                  <h4>🏢 Real Estate & Photography</h4>
                  <p>
                    Process property photos, portfolio images, and client
                    galleries with consistent quality settings while reducing
                    storage and bandwidth costs.
                  </p>
                  <div className={styles.industryStats}>
                    <span>Typical Savings: 55-75%</span>
                    <span>Batch Size: 20-100 images</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Technical Advantages */}
            <div className={styles.contentBlock}>
              <h2>Advanced Batch Processing Technology</h2>
              <div className={styles.techFeatures}>
                <div className={styles.techFeature}>
                  <h4>🔄 Parallel Processing Architecture</h4>
                  <p>
                    Our bulk compressor utilizes optimized parallel processing
                    that handles multiple images simultaneously without
                    compromising quality. Unlike sequential processing that
                    bottlenecks performance, our system maintains consistent
                    speed regardless of batch size.
                  </p>
                </div>
                <div className={styles.techFeature}>
                  <h4>🎯 Intelligent Format Detection</h4>
                  <p>
                    Automatically analyzes each image's content type and applies
                    optimal compression algorithms. Photographs receive
                    different treatment than graphics, ensuring maximum
                    efficiency for each image type within your batch.
                  </p>
                </div>
                <div className={styles.techFeature}>
                  <h4>📐 Smart Resizing Algorithms</h4>
                  <p>
                    Batch resize images while maintaining aspect ratios and
                    visual quality. Our algorithms prevent distortion and ensure
                    professional results across all resized images in your
                    batch.
                  </p>
                </div>
                <div className={styles.techFeature}>
                  <h4>⚡ Memory-Efficient Processing</h4>
                  <p>
                    Optimized memory management allows processing large batches
                    without browser crashes or performance degradation, even
                    with high-resolution images.
                  </p>
                </div>
              </div>
            </div>

            {/* Compression Strategies */}
            <div className={styles.contentBlock}>
              <h2>Professional Compression Strategies</h2>
              <div className={styles.strategyGrid}>
                <div className={styles.strategy}>
                  <h4>🎨 Quality-Based Strategy</h4>
                  <p>
                    <strong>Best for:</strong> Mixed content batches
                    <br />
                    <strong>Settings:</strong> 75-85% quality
                    <br />
                    <strong>Results:</strong> Balanced size/quality ratio across
                    different image types
                  </p>
                </div>
                <div className={styles.strategy}>
                  <h4>💨 Maximum Compression</h4>
                  <p>
                    <strong>Best for:</strong> Web optimization
                    <br />
                    <strong>Settings:</strong> 60-70% quality + WebP format
                    <br />
                    <strong>Results:</strong> Smallest file sizes for fastest
                    loading
                  </p>
                </div>
                <div className={styles.strategy}>
                  <h4>🖼️ Archive Quality</h4>
                  <p>
                    <strong>Best for:</strong> Photography portfolios
                    <br />
                    <strong>Settings:</strong> 90-95% quality + resize to 1920px
                    <br />
                    <strong>Results:</strong> Near-original quality with
                    reasonable file sizes
                  </p>
                </div>
                <div className={styles.strategy}>
                  <h4>📱 Mobile Optimization</h4>
                  <p>
                    <strong>Best for:</strong> App assets & mobile sites
                    <br />
                    <strong>Settings:</strong> 70-80% quality + resize to 1200px
                    <br />
                    <strong>Results:</strong> Optimized for mobile data and
                    performance
                  </p>
                </div>
              </div>
            </div>

            {/* File Format Guide */}
            <div className={styles.contentBlock}>
              <h2>Choosing the Right Output Format</h2>
              <div className={styles.formatComparison}>
                <div className={styles.formatOption}>
                  <h4>JPG Format</h4>
                  <ul>
                    <li>
                      <strong>Best for:</strong> Photographs, product images
                    </li>
                    <li>
                      <strong>Compression:</strong> Lossy (adjustable quality)
                    </li>
                    <li>
                      <strong>Transparency:</strong> No
                    </li>
                    <li>
                      <strong>Browser Support:</strong> 100%
                    </li>
                    <li>
                      <strong>Typical Savings:</strong> 60-85%
                    </li>
                  </ul>
                </div>
                <div className={styles.formatOption}>
                  <h4>WebP Format</h4>
                  <ul>
                    <li>
                      <strong>Best for:</strong> Modern websites, performance
                    </li>
                    <li>
                      <strong>Compression:</strong> Superior to JPG
                    </li>
                    <li>
                      <strong>Transparency:</strong> Yes
                    </li>
                    <li>
                      <strong>Browser Support:</strong> 97% (modern browsers)
                    </li>
                    <li>
                      <strong>Typical Savings:</strong> 70-90%
                    </li>
                  </ul>
                </div>
                <div className={styles.formatOption}>
                  <h4>PNG Format</h4>
                  <ul>
                    <li>
                      <strong>Best for:</strong> Graphics, logos, text
                    </li>
                    <li>
                      <strong>Compression:</strong> Lossless
                    </li>
                    <li>
                      <strong>Transparency:</strong> Yes (alpha channel)
                    </li>
                    <li>
                      <strong>Browser Support:</strong> 100%
                    </li>
                    <li>
                      <strong>Typical Savings:</strong> 20-50%
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className={styles.contentBlock}>
              <h2>Expected Performance Results</h2>
              <div className={styles.performanceMetrics}>
                <div className={styles.metric}>
                  <h4>Time Savings</h4>
                  <p>
                    Processing 20 images takes approximately{" "}
                    <strong>15-30 seconds</strong> compared to{" "}
                    <strong>10-15 minutes</strong> of manual work - a{" "}
                    <strong>95% reduction</strong> in processing time.
                  </p>
                </div>
                <div className={styles.metric}>
                  <h4>Storage Reduction</h4>
                  <p>
                    Typical batch compression reduces total storage requirements
                    by <strong>65-80%</strong>. A 500MB image collection
                    typically compresses to <strong>100-175MB</strong>.
                  </p>
                </div>
                <div className={styles.metric}>
                  <h4>Bandwidth Optimization</h4>
                  <p>
                    Compressed images reduce page load times by{" "}
                    <strong>40-70%</strong> and decrease bandwidth costs
                    proportionally to your traffic volume.
                  </p>
                </div>
                <div className={styles.metric}>
                  <h4>Quality Retention</h4>
                  <p>
                    At 80% quality settings, visual quality remains{" "}
                    <strong>virtually indistinguishable</strong> from originals
                    while achieving significant size reduction.
                  </p>
                </div>
              </div>
            </div>

            {/* Best Practices */}
            <div className={styles.contentBlock}>
              <h2>Bulk Compression Best Practices</h2>
              <div className={styles.bestPractices}>
                <div className={styles.practice}>
                  <h4>📁 Organize Before Processing</h4>
                  <p>
                    Group similar images together (all product photos, all blog
                    images) to apply consistent settings. Mixed content may
                    require separate batches for optimal results.
                  </p>
                </div>
                <div className={styles.practice}>
                  <h4>⚙️ Test Settings on Samples</h4>
                  <p>
                    Before processing large batches, test compression settings
                    on 2-3 sample images to verify quality meets your
                    requirements.
                  </p>
                </div>
                <div className={styles.practice}>
                  <h4>📐 Use Smart Resizing</h4>
                  <p>
                    Enable resizing with maximum dimensions appropriate for your
                    use case. Website images rarely need resolutions above
                    1920px width.
                  </p>
                </div>
                <div className={styles.practice}>
                  <h4>🔍 Review Results Systematically</h4>
                  <p>
                    Use the built-in comparison tools to verify compression
                    results before downloading entire batches.
                  </p>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className={styles.contentBlock}>
              <h2>Frequently Asked Questions</h2>
              <div className={styles.faqGrid}>
                <div className={styles.faqItem}>
                  <h4>How many images can I process at once?</h4>
                  <p>
                    Our bulk compressor supports up to 20 images per batch for
                    optimal performance and browser stability. For larger
                    collections, we recommend processing in multiple batches of
                    20 images each.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Will bulk compression reduce image quality?</h4>
                  <p>
                    With proper quality settings (75-85%), visual quality loss
                    is minimal to imperceptible. The compression algorithm is
                    optimized to eliminate unnecessary data while preserving
                    visual integrity.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Can I process different image types in one batch?</h4>
                  <p>
                    Yes, the bulk compressor handles mixed formats (JPG, PNG,
                    WebP) simultaneously and applies appropriate compression
                    algorithms for each image type automatically.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>How long does batch processing take?</h4>
                  <p>
                    Processing time depends on image sizes and quantity. Typical
                    batches of 20 images process in 15-30 seconds. Large
                    high-resolution images may take slightly longer.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Are my images secure during processing?</h4>
                  <p>
                    All processing occurs entirely in your browser - images
                    never leave your computer. We don't upload files to external
                    servers, ensuring complete privacy and security.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Can I customize settings for individual images?</h4>
                  <p>
                    Currently, settings apply uniformly to the entire batch. For
                    individual customization, process images separately or split
                    your batch into groups with similar requirements.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}

      {/* Settings Changed Notice */}
      {settingsChanged && (
        <div className={styles.settingsChangedNotice}>
          ⚠️ Settings have changed. Click "Re-compress with New Settings" to
          apply changes.
        </div>
      )}

      {/* Main Content */}
      {images.length > 0 && (
        <div className={styles.editorSection}>
          <div className={styles.mainContent}>
            {/* Left Side - Controls & Images */}
            <div className={styles.controlsSide}>
              <div className={styles.sectionHeader}>
                <h3>Selected Images ({images.length})</h3>
                <button
                  onClick={resetAll}
                  className={styles.changeImageButton}
                  type="button"
                >
                  Clear All
                </button>
              </div>

              {/* Images Grid */}
              <div className={styles.imagesGrid}>
                {images.map((image, index) => (
                  <div key={image.id} className={styles.imageThumbnail}>
                    <div className={styles.thumbnailHeader}>
                      <span className={styles.imageIndex}>{index + 1}</span>
                      <button
                        onClick={() => removeImage(image.id)}
                        className={styles.removeButton}
                        title="Remove image"
                        type="button"
                      >
                        ×
                      </button>
                    </div>
                    <img src={image.url} alt={`Original ${index}`} />
                    <div className={styles.imageInfo}>
                      <span className={styles.fileName}>{image.name}</span>
                      <span className={styles.fileSize}>
                        {formatFileSize(image.size)}
                      </span>
                      <span className={styles.dimensions}>
                        {image.dimensions.width}×{image.dimensions.height}
                      </span>
                    </div>
                    {batchProgress[image.id] && (
                      <div className={styles.progressLabel}>
                        {batchProgress[image.id]}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Compression Settings */}
              <div className={styles.settingsPanel}>
                <h4>Compression Settings</h4>

                {/* Format Selection */}
                <div className={styles.settingGroup}>
                  <label>Output Format:</label>
                  <select
                    value={outputFormat}
                    onChange={(e) => {
                      setOutputFormat(e.target.value);
                      if (compressedImages.length > 0) {
                        setSettingsChanged(true);
                      }
                    }}
                    className={styles.formatSelect}
                  >
                    <option value="jpg">JPG (Best for photos)</option>
                    <option value="webp">WebP (Modern & efficient)</option>
                    <option value="png">PNG (Best for graphics)</option>
                  </select>
                </div>

                {/* Quality Control */}
                <div className={styles.settingGroup}>
                  <label htmlFor="qualitySlider">
                    Compression Quality: {quality}%
                    <span className={styles.qualityHint}>
                      {quality >= 80
                        ? " (High Quality)"
                        : quality >= 60
                        ? " (Balanced)"
                        : " (Maximum Compression)"}
                    </span>
                  </label>
                  <input
                    type="range"
                    id="qualitySlider"
                    min="10"
                    max="100"
                    value={quality}
                    onChange={(e) => {
                      setQuality(parseInt(e.target.value));
                      if (compressedImages.length > 0) {
                        setSettingsChanged(true);
                      }
                    }}
                    className={styles.qualitySlider}
                  />
                </div>

                {/* Resize Options */}
                <div className={styles.settingGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={resizeEnabled}
                      onChange={(e) => {
                        setResizeEnabled(e.target.checked);
                        if (compressedImages.length > 0) {
                          setSettingsChanged(true);
                        }
                      }}
                    />
                    Resize Images
                  </label>

                  {resizeEnabled && (
                    <div className={styles.resizeOptions}>
                      <div className={styles.dimensionInputs}>
                        <div className={styles.inputGroup}>
                          <label>Max Width (px)</label>
                          <input
                            type="number"
                            value={maxWidth}
                            onChange={(e) => {
                              setMaxWidth(parseInt(e.target.value) || 1920);
                              if (compressedImages.length > 0) {
                                setSettingsChanged(true);
                              }
                            }}
                            min="100"
                            max="10000"
                          />
                        </div>
                        <div className={styles.inputGroup}>
                          <label>Max Height (px)</label>
                          <input
                            type="number"
                            value={maxHeight}
                            onChange={(e) => {
                              setMaxHeight(parseInt(e.target.value) || 1080);
                              if (compressedImages.length > 0) {
                                setSettingsChanged(true);
                              }
                            }}
                            min="100"
                            max="10000"
                          />
                        </div>
                      </div>
                      <label className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={maintainAspectRatio}
                          onChange={(e) => {
                            setMaintainAspectRatio(e.target.checked);
                            if (compressedImages.length > 0) {
                              setSettingsChanged(true);
                            }
                          }}
                        />
                        Maintain aspect ratio
                      </label>
                    </div>
                  )}
                </div>

                {/* Compress Button */}
                <div className={styles.buttonGroup}>
                  <button
                    onClick={compressBatch}
                    className={styles.compressButton}
                    disabled={isCompressButtonDisabled}
                    type="button"
                  >
                    {getCompressButtonText()}
                  </button>
                  {isCompressButtonDisabled && (
                    <p className={styles.compressHint}>
                      Change settings above to re-compress with new parameters
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Side - Results */}
            {compressedImages.length > 0 && (
              <div className={styles.resultsSide}>
                <div className={styles.resultsSection}>
                  <h3>Compression Results</h3>

                  {/* Summary */}
                  <div className={styles.batchSummary}>
                    <h4>Compression Summary</h4>
                    <div className={styles.summaryItem}>
                      <span>Total Images:</span>
                      <strong>{compressedImages.length}</strong>
                    </div>
                    <div className={styles.summaryItem}>
                      <span>Original Size:</span>
                      <strong>{formatFileSize(totalOriginalSize)}</strong>
                    </div>
                    <div className={styles.summaryItem}>
                      <span>Compressed Size:</span>
                      <strong>{formatFileSize(totalCompressedSize)}</strong>
                    </div>
                    <div className={styles.summaryItem}>
                      <span>Total Savings:</span>
                      <strong className={styles.savings}>
                        {formatFileSize(totalSavings)} (
                        {Math.round((totalSavings / totalOriginalSize) * 100)}%)
                      </strong>
                    </div>
                    <div className={styles.summaryItem}>
                      <span>Average per Image:</span>
                      <strong>{Math.round(averageSavings)}% smaller</strong>
                    </div>
                  </div>

                  {/* Download Buttons */}
                  <div className={styles.downloadSection}>
                    <button
                      onClick={downloadAllImages}
                      className={styles.downloadButton}
                      type="button"
                    >
                      📥 Download All Compressed Images
                    </button>
                    <p className={styles.downloadHelp}>
                      Files will download one by one
                    </p>
                  </div>

                  {/* Compressed Images List */}
                  <div className={styles.compressedList}>
                    <h4>Compressed Files:</h4>
                    {compressedImages.map((compressedImage, index) => (
                      <div key={index} className={styles.compressedItem}>
                        <div className={styles.compressedPreview}>
                          <img
                            src={compressedImage.url}
                            alt={`Compressed ${index}`}
                          />
                        </div>
                        <div className={styles.compressedInfo}>
                          <span className={styles.compressedName}>
                            {compressedImage.name}
                          </span>
                          <div className={styles.sizeComparison}>
                            <span className={styles.originalSize}>
                              {formatFileSize(compressedImage.original.size)}
                            </span>
                            <span className={styles.arrow}>→</span>
                            <span className={styles.compressedSize}>
                              {formatFileSize(compressedImage.size)}
                            </span>
                            <span className={styles.savingsBadge}>
                              {compressedImage.savings}% smaller
                            </span>
                          </div>
                          <div className={styles.dimensionsInfo}>
                            {compressedImage.dimensions.width}×
                            {compressedImage.dimensions.height}
                            {compressedImage.dimensions.width !==
                              compressedImage.original.dimensions.width && (
                              <span className={styles.resizedNote}>
                                {" "}
                                (resized)
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => downloadImage(compressedImage)}
                          className={styles.downloadSingleButton}
                          type="button"
                        >
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkImageCompressor;
